import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createVNode as _createVNode, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
}

import { processClasses } from "@oculai/planner";
import {
  useProjectDurationSettings,
  durationService,
  useCurrentCustomerName,
  useCurrentSiteId,
  useIsWorkingDay,
  useNonWorkingDaysByDaySet,
  OutagesByRange,
  CameraRepository,
} from "@oculai/planner";
import { differenceInHours } from "date-fns";
import format from "date-fns/format";
import parse from "date-fns/parse";
import { Chart } from "highcharts-vue";
import { watch, ref, PropType, computed } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/UI/LoadingSpinner.vue";
import "@/composables/process";
import { useHasProjectFeature } from "@/composables/project";
import { useStreams } from "@/composables/stream";
import { apiClient } from "@/repositories/clients";
import { HierarchyType, HierarchyTagStore } from "@/types/HierarchyTag";
import { CycleTimesReport, ShortenedProcess, ProcessReport } from "@/types/Process";
import {
  calculateActiveDuration,
  calculateWorkHoursSum,
} from "@/views/analytics/ProcessTable/utils";


export default /*#__PURE__*/_defineComponent({
  __name: 'CycleTimesPlot',
  props: {
  data: {
    type: Object as PropType<CycleTimesReport>,
    required: true,
  },
  report: {
    type: Object as PropType<ProcessReport>,
    required: true,
  },
  hierarchy: {
    type: Array as PropType<HierarchyTagStore[]>,
    required: true,
  },
},
  setup(__props) {

const { t } = useI18n();

const props = __props;

const customerName = useCurrentCustomerName();
const siteId = useCurrentSiteId();
const { streams } = useStreams();
const nonWorkingDaysByDaySet = useNonWorkingDaysByDaySet(apiClient);
const isWorkingDay = useIsWorkingDay(apiClient);
const outages = ref<OutagesByRange>({});
const rawData = ref<CycleTimesReport>([]);
const areOutagesLoading = ref(false);
const chartKey = ref(0);
const workingHoursFeatureEnabled = useHasProjectFeature("working_hours");

const { projectDurationSettings } = useProjectDurationSettings(apiClient);

const durationSettings = computed(() =>
  projectDurationSettings.value
    ? durationService.calculateSettings(
        projectDurationSettings.value.working_hours,
        projectDurationSettings.value.non_working_days,
      )
    : null,
);

const seriesColors = {
  planned: "#BCCFCC",
  active: "#336260",
  inactive: "#991B1B",
  utilization: "#E7BC66",
  outages: "#FCA5A5",
} as const;

const defaultChartOptions = {
  chart: {
    zoomType: "x",
    reflow: true,
    height: 600,
  },
  global: {
    lang: {
      resetZoom: "he",
    },
  },
  title: {
    text: undefined,
  },
  exporting: {
    enabled: false,
  },
  plotOptions: {
    series: {
      states: {
        inactive: {
          opacity: 1,
        },
      },
    },
  },
  yAxis: [
    {
      title: {
        text: t("analytics.reports.days"),
      },
    },
  ],
  accessibility: {
    enabled: true,
  },
};

const getOutages = async (processData: CycleTimesReport[number]["processes"]) => {
  const start = new Date(processData[0].start_time);
  const end = new Date(processData[0].end_time);

  processData.forEach((process) => {
    if (process.start_time < start) {
      start.setTime(process.start_time.getTime());
    }

    if (process.end_time > end) {
      end.setTime(process.end_time.getTime());
    }
  });

  const outagesValue = await CameraRepository(apiClient).loadOutagesByRange(
    customerName,
    siteId,
    start,
    end,
  );

  const remappedOutagesValue = Object.fromEntries(
    Object.entries(outagesValue).map(([key, outages]) => {
      const date = parse(key, "yyyy-MM-dd", new Date());
      const factor = 0.5;
      if (nonWorkingDaysByDaySet.value?.has(key) || !isWorkingDay.value(date)) {
        return [key, []];
      }

      const out = outages.filter((entry) => {
        const outageHours = differenceInHours(entry.end_time, entry.start_time);
        const tmlHours = differenceInHours(entry.tml_end, entry.tml_start);
        return outageHours > tmlHours * factor;
      });
      if (out.length > streams.value.length * factor) {
        return [key, out];
      } else {
        return [key, []];
      }
    }),
  );

  return remappedOutagesValue;
};

watch(
  () => props.data,
  async (data) => {
    rawData.value = data;

    if (!data.length) {
      return;
    }

    outages.value = {};
    areOutagesLoading.value = true;
    outages.value = await getOutages(data.flatMap((item) => item.processes));
    areOutagesLoading.value = false;
  },
  { immediate: true },
);

watch(
  () => props.report,
  () => {
    chartKey.value += 1;
  },
);

const transformRawData = (data: CycleTimesReport) => {
  if (!data) {
    return defaultChartOptions;
  }

  const report = props.report as unknown as ProcessReport<"cycle_times">;

  let shouldShowPlannedChart = false;

  if (
    report.processes.mode === "single_process" &&
    (!report.processes.single_processes.length ||
      report.processes.single_processes.length === processClasses.length)
  ) {
    shouldShowPlannedChart = true;
  } else if (report.processes.mode === "component") {
    const isAggregatedBySection = report.plot.config.aggregation_level === "section";
    const isAggregatedByPreset =
      props.hierarchy.filter((tag) => {
        const componentToCompare = report.processes.components.find((component) => {
          return component.id === tag._id;
        });

        if (!componentToCompare) {
          return false;
        }

        const isAllProcessesSelected = tag.attached_processes.every((process) => {
          return componentToCompare.processes.includes(process);
        });

        return tag.type === "component" && isAllProcessesSelected;
      }).length === 1;

    const noProcessSelected = !report.processes.components.some((component) => {
      return component.processes.length;
    });

    shouldShowPlannedChart = (isAggregatedBySection && isAggregatedByPreset) || noProcessSelected;
  }

  const aggregatedData = makeDataAggregation(data, report.plot.config.aggregation_level);
  const chartOptionsValue = getChartOptions(
    aggregatedData,
    shouldShowPlannedChart && report.plot.config.show_planned,
    report.plot.config,
  );

  return chartOptionsValue;
};

const makeDataAggregation = (data: CycleTimesReport, aggregationLevel: HierarchyType) => {
  const aggregations = {} as Record<string, CycleTimesReport>;

  data.forEach((item) => {
    const keys = [];
    const building = item.section_mask_mapping.building_name;
    const level = item.section_mask_mapping.level_name;
    const section = item.section_mask_mapping.section_name;

    if (aggregationLevel === "building") {
      keys.push(building);
    } else if (aggregationLevel === "level") {
      keys.push(building, level);
    } else if (aggregationLevel === "section") {
      keys.push(building, level, section);
    }

    const key = keys.filter(Boolean).join(" - ");

    if (!aggregations[key]) {
      aggregations[key] = [];
    }

    aggregations[key].push(item);
  });

  const prettifiedAggregation = Object.entries(aggregations).reduce(
    (acc, [key, value]) => {
      acc.push({
        name: key,
        processes: value.flatMap((item) => item.processes as ShortenedProcess[]),
        planned_events: value.map((item) => item.planned_event),
        is_active: value.some((item) => item.is_active),
      });

      return acc;
    },
    [] as {
      name: string;
      processes: ShortenedProcess[];
      planned_events: { start: Date; end: Date }[];
      is_active: boolean;
    }[],
  );

  return prettifiedAggregation;
};

const calculateMinimumNeededWorkersOnSiteByIntervals = (
  intervals: CycleTimesReport[number]["processes"][number]["work_intervals"],
): number => {
  const processes = intervals
    .slice()
    .sort((a, b) => a.start_time.getTime() - b.start_time.getTime());

  let totalWorkers = 0;
  let freeWorkers = 0;
  const activeProcesses: typeof intervals = [];

  processes.forEach((process) => {
    activeProcesses.forEach((pastProcess, index) => {
      if (process.start_time >= pastProcess.end_time) {
        activeProcesses.splice(index, 1);

        freeWorkers += pastProcess.workforce.validated_count;
      }
    });

    const neededWorkers = freeWorkers - process.workforce.validated_count;

    if (neededWorkers >= 0) {
      freeWorkers -= process.workforce.validated_count;
    } else {
      freeWorkers = 0;
      totalWorkers += Math.abs(neededWorkers);
    }

    activeProcesses.push(process);
  });

  return totalWorkers;
};

const calculateCapacityPerShift = (date: string, processes: ShortenedProcess[]) => {
  const weekDay = parse(date, "yyyy-MM-dd", new Date()).getDay();

  const shiftDuration = durationSettings.value?.workingDaysAndHoursMap[weekDay]?.workingHours || 0;

  if (!shiftDuration) {
    return 0;
  }

  const minimumNeededWorkers = calculateMinimumNeededWorkersOnSiteByIntervals(
    processes.flatMap((process) => process.work_intervals),
  );

  return minimumNeededWorkers * shiftDuration;
};

const getAverage = (arr: number[]): number => {
  if (!arr.length) {
    return 0;
  }

  return arr.reduce((acc, val) => acc + val, 0) / arr.length;
};

const preProcessValidatedCount = (processes: ShortenedProcess[]) => {
  const MINIMUM_VALIDATED_COUNT_RATIO = 0.8;

  const intervals = processes.flatMap((process) => process.work_intervals);

  const intervalWithValidatedCount = intervals.filter((interval) => {
    return interval.workforce.validated_count;
  });

  if (intervalWithValidatedCount.length / intervals.length < MINIMUM_VALIDATED_COUNT_RATIO) {
    return;
  }

  const sumOfValidatedCounts = intervalWithValidatedCount.reduce((acc, interval) => {
    return acc + interval.workforce.validated_count;
  }, 0);

  const averageValidatedCount = sumOfValidatedCounts / intervalWithValidatedCount.length;

  return processes.map((process) => {
    return {
      ...process,
      work_intervals: process.work_intervals.map((interval) => {
        return {
          ...interval,
          workforce: {
            ...interval.workforce,
            validated_count: interval.workforce.validated_count || averageValidatedCount,
          },
        };
      }),
    };
  });
};

const calculateUtilization = (dayDuration: number, processes: ShortenedProcess[]) => {
  const mappedProcesses = preProcessValidatedCount(processes);

  if (!mappedProcesses) {
    return;
  }

  const groups = processes.reduce((acc, process) => {
    const key = process.date;

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(process);

    return acc;
  }, {} as Record<string, ShortenedProcess[]>);

  const workHours = calculateWorkHoursSum(processes);

  const capacities = Object.entries(groups).map(([date, processes]) => {
    return calculateCapacityPerShift(date, processes);
  });

  const utilization = Math.round((workHours / (dayDuration * getAverage(capacities))) * 100);

  return utilization;
};

const calculateOutages = (startTime: Date, endTime: Date, processes: ShortenedProcess[]) => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  let outagesDuration = 0;

  const processesPerDate = processes.reduce((acc, process) => {
    if (!acc[process.date]) {
      acc[process.date] = [];
    }

    acc[process.date].push(process);

    return acc;
  }, {} as Record<string, ShortenedProcess[]>);

  for (let date = start; date <= end; date.setDate(date.getDate() + 1)) {
    const stringDate = format(date, "yyyy-MM-dd");

    if (!processesPerDate[stringDate]?.length && outages.value[stringDate]?.length) {
      outagesDuration++;
    }
  }

  return outagesDuration;
};

const getChartOptions = (
  data: {
    name: string;
    processes: ShortenedProcess[];
    planned_events: { start: Date; end: Date }[];
    is_active: boolean;
  }[],
  showPlannedChart: boolean,
  config: ProcessReport<"cycle_times">["plot"]["config"],
) => {
  const categories = data.map((item) => item.name);
  const activeCategories = new Set(data.map((item, i) => item.is_active && i));

  const plannedDuration: (number | string)[][] = [];
  const inactiveDuration: (number | string)[][] = [];
  const activeDuration: (number | string)[][] = [];
  const utilization: (number | string | undefined)[][] = [];
  const outagesDuration: (number | string)[][] = [];

  data.forEach((item) => {
    const total = { days: 0, hours: 0 };
    let planned = 0;

    if (!durationSettings.value) {
      return defaultChartOptions;
    }

    const firstProcess = new Date();
    const lastProcess = new Date(0);
    const firstEvent = new Date();
    const lastEvent = new Date(0);

    item.processes.forEach((process) => {
      if (process.start_time < firstProcess) {
        firstProcess.setTime(process.start_time.getTime());
      }

      if (process.end_time > lastProcess) {
        lastProcess.setTime(process.end_time.getTime());
      }
    });

    item.planned_events.forEach((event) => {
      if (event.start < firstEvent) {
        firstEvent.setTime(event.start.getTime());
      }

      if (event.end > lastEvent) {
        lastEvent.setTime(event.end.getTime());
      }
    });

    const totalDuration = durationService.calculateDuration(
      durationSettings.value,
      firstProcess,
      lastProcess,
    );

    total.days = totalDuration.workingDays;
    total.hours = totalDuration.workingHours;

    planned = durationService.calculateDuration(
      durationSettings.value,
      firstEvent,
      lastEvent,
    ).workingDays;

    const formattedStart = format(firstProcess, "dd.MM.yyyy");
    const formattedEnd = format(lastProcess, "dd.MM.yyyy");
    const active = calculateActiveDuration(durationSettings.value, item.processes);

    const utilizationValue = calculateUtilization(total.days, item.processes);
    const outageDuration = calculateOutages(firstProcess, lastProcess, item.processes);
    const inactive = Math.max(total.days - active.days - outageDuration, 0);

    const metadata = [formattedStart, formattedEnd, total.days];

    plannedDuration.push([planned, t("analytics.reports.days"), ...metadata]);
    inactiveDuration.push([inactive, t("analytics.reports.days"), ...metadata]);
    activeDuration.push([active.days, t("analytics.reports.days"), ...metadata]);
    utilization.push([utilizationValue, "%", ...metadata]);
    outagesDuration.push([outageDuration, t("analytics.reports.days"), ...metadata]);
  });

  const seriesConfig = {
    keys: ["y", "suffix", "start", "end", "total"],
    visible: !areOutagesLoading.value,
  };

  const series = [];

  if (showPlannedChart) {
    series.push({
      name: t("analytics.reports.planned"),
      data: plannedDuration,
      color: seriesColors.planned,
      type: "column",
      stacking: undefined,
      yAxis: 0,
      ...seriesConfig,
    });
  }

  if (config.show_outages) {
    series.push({
      name: t("analytics.reports.outages"),
      data: outagesDuration,
      color: seriesColors.outages,
      stacking: "normal",
      type: "column",
      yAxis: 0,
      tooltip: {
        valueSuffix: ` ${t("analytics.reports.days")}`,
      },
      ...seriesConfig,
    });
  }

  if (config.show_inactive_days) {
    series.push({
      name: t("analytics.reports.inactive"),
      data: inactiveDuration,
      color: seriesColors.inactive,
      stacking: "normal",
      type: "column",
      yAxis: 0,
      tooltip: {
        valueSuffix: ` ${t("analytics.reports.days")}`,
      },
      ...seriesConfig,
    });
  }

  if (config.show_active_days) {
    series.push({
      name: t("analytics.reports.active"),
      data: activeDuration,
      color: seriesColors.active,
      stacking: "normal",
      type: "column",
      yAxis: 0,
      tooltip: {
        valueSuffix: ` ${t("analytics.reports.days")}`,
      },
      ...seriesConfig,
    });
  }

  if (workingHoursFeatureEnabled && config.show_utilization) {
    series.push({
      name: t("analytics.reports.utilization"),
      data: utilization,
      color: seriesColors.utilization,
      type: "spline",
      yAxis: 1,
      tooltip: {
        valueSuffix: "%",
      },
      lineWidth: 2,
      zIndex: 1,
      ...seriesConfig,
    });
  }

  return {
    ...defaultChartOptions,
    xAxis: {
      categories,
      labels: {
        useHTML: true,
        style: {
          whiteSpace: "no-wrap",
        },
        autoRotationLimit: 1000,
        formatter: function () {
          const position = (this as unknown as { pos: number }).pos;
          const value = (this as unknown as { value: string }).value;

          if (activeCategories.has(position)) {
            return `<span><span class="text-green">●</span> ${value}</span>`;
          }

          return value;
        },
      },
      crosshair: true,
    },
    yAxis: [
      ...defaultChartOptions.yAxis,
      ...(workingHoursFeatureEnabled && config.show_utilization
        ? [
            {
              title: {
                text: t("analytics.reports.utilization"),
              },
              labels: {
                format: "{value}%",
              },
              min: 0,
              opposite: true,
            },
          ]
        : []),
    ],
    series: series,
    tooltip: {
      shared: true,
      useHTML: true,
      outside: true,
      positioner: (labelWidth: number, labelHeight: number, point: Record<string, number>) => ({
        x: point.plotX - labelWidth * 0.9,
        y: point.plotY,
      }),
      formatter: function () {
        const tooltipContent = (
          this as unknown as { points: Record<string, Record<string, unknown>>[] }
        ).points.reduce(function (s, point, i) {
          if (i === 0) {
            s += `
          <p>
            ${t("analytics.processes.start")}: <span class="font-bold">${point.point.start}</span>
          </p>
          <p>${t("analytics.processes.end")}: <span class="font-bold">${
              activeCategories.has(point.point.x as number)
                ? `<span class="inline-block font-light text-xs rounded-md px-2 py-0 bg-green text-white">
                      ${t("analytics.reports.in_progress")}
                    </span>`
                : point.point.end
            }</span></p>
          <p class="mb-1">
            ${t("analytics.reports.total")}: <span class="font-bold">
            ${point.point.total} ${t("analytics.reports.days")}</span>
          </p>`;
          }

          return `${s}<p>
            <span style="color: ${point.color}">●</span>
              ${point?.series?.name}:
              <span class="font-bold">${point.y} ${point.point.suffix}</span>
          </p>`;
        }, "<p class='text-sm mb-2 pr-1 border-b border-gray-200'>" +
          (this as unknown as { x: string }).x +
          "</p>");

        return `<div class="p-1">${tooltipContent}</div>`;
      },
    },
  };
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_unref(Chart), {
      options: transformRawData(rawData.value),
      class: "font-light text-b",
      key: chartKey.value
    }, null, 8, ["options"])),
    (areOutagesLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(LoadingSpinner)
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})